import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Check, Upload, Link, Video, Loader2, Play, Pause,
  Volume2, VolumeX, RotateCcw, Repeat, Clock, AlertCircle
} from 'lucide-react';
import { HardDrive } from 'lucide-react';
import ReactPlayer from 'react-player';
import uploadService from '../../../services/uploadService';
import { AssetLibraryModal } from '../assets';

const ACCEPTED_FORMATS = '.mp4,.mov,.webm';
const MAX_FILE_SIZE_MB = 1024; // 1 GB
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const AUDIO_MODES = [
  { id: 'normal', name: 'Normal', desc: 'Full volume', icon: Volume2 },
  { id: 'background', name: 'Background', desc: 'Duck other audio', icon: Volume2 },
  { id: 'muted', name: 'Muted', desc: 'No sound', icon: VolumeX },
];

const DISPLAY_MODES = [
  { id: 'fullscreen', name: 'Full Screen', desc: 'Video fills the stage' },
  { id: 'pip', name: 'Picture in Picture', desc: 'Small overlay' },
  { id: 'background', name: 'Background', desc: 'Behind participants' },
];

// Custom Select component with portal for dropdown
const Select = ({ value, onValueChange, children, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectRef.current && !selectRef.current.contains(event.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(event.target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen || !selectRef.current) return;
    const updatePosition = () => {
      const rect = selectRef.current.getBoundingClientRect();
      const dropdownHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < dropdownHeight && rect.top > spaceBelow;
      setDropdownStyle({
        position: 'fixed', left: rect.left, width: rect.width, zIndex: 99999,
        ...(openUpward ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => { window.removeEventListener('scroll', updatePosition, true); window.removeEventListener('resize', updatePosition); };
  }, [isOpen]);

  const options = React.Children.toArray(children);
  const selectedOption = options.find(opt => opt.props.value === value);

  return (
    <div className="relative" ref={selectRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:border-white/20 transition-colors"
      >
        <span className={selectedOption ? '' : 'text-gray-500'}>
          {selectedOption ? selectedOption.props.children : placeholder}
        </span>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && ReactDOM.createPortal(
        <div ref={dropdownRef} style={dropdownStyle} className="bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl py-1 max-h-60 overflow-y-auto">
          {options.map((option) => (
            <button
              key={option.props.value}
              type="button"
              onClick={() => { onValueChange(option.props.value); setIsOpen(false); }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                option.props.value === value ? 'text-[#6a1fbf]' : 'text-gray-300'
              }`}
            >
              {option.props.children}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

const SelectItem = ({ value, children }) => null;

// Custom Switch component
const Switch = ({ checked, onCheckedChange }) => (
  <button
    type="button"
    onClick={() => onCheckedChange(!checked)}
    className={`relative w-11 h-6 rounded-full transition-colors ${
      checked ? 'bg-[#6a1fbf]' : 'bg-white/20'
    }`}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

// Custom Slider component
const Slider = ({ value, onValueChange, min, max, step }) => {
  const percentage = ((value[0] - min) / (max - min)) * 100;
  return (
    <div className="relative h-6 flex items-center">
      <div className="w-full h-2 bg-white/20 rounded-full">
        <div className="h-full bg-[#6a1fbf] rounded-full" style={{ width: `${percentage}%` }} />
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value[0]}
        onChange={(e) => onValueChange([parseInt(e.target.value)])}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
      <div
        className="absolute w-4 h-4 bg-white rounded-full shadow-md pointer-events-none"
        style={{ left: `calc(${percentage}% - 8px)` }}
      />
    </div>
  );
};

// Tabs components
const Tabs = ({ value, onValueChange, children, className }) => {
  return (
    <div className={className}>
      {React.Children.map(children, child => {
        if (child.type === TabsList) {
          return React.cloneElement(child, { activeTab: value, setActiveTab: onValueChange });
        }
        if (child.type === TabsContent) {
          return child.props.value === value ? child : null;
        }
        return child;
      })}
    </div>
  );
};

const TabsList = ({ children, className, activeTab, setActiveTab }) => (
  <div className={`flex ${className}`}>
    {React.Children.map(children, child =>
      React.cloneElement(child, { activeTab, setActiveTab })
    )}
  </div>
);

const TabsTrigger = ({ value, children, className, activeTab, setActiveTab }) => (
  <button
    type="button"
    onClick={() => setActiveTab(value)}
    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors rounded-lg flex items-center justify-center gap-2 ${
      activeTab === value
        ? 'bg-white/10 text-white'
        : 'text-gray-400 hover:text-white'
    } ${className || ''}`}
  >
    {children}
  </button>
);

const TabsContent = ({ children, className }) => (
  <div className={className}>{children}</div>
);

function VideoPreview({ src, duration, fileSize, onRemove, onReplace }) {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  const isEmbeddable = ReactPlayer.canPlay(src);

  const formatTime = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    if (isEmbeddable) {
      setPlaying(!playing);
      return;
    }
    if (videoRef.current) {
      if (playing) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setPlaying(!playing);
    }
  };

  return (
    <div className="bg-white/5 rounded-xl overflow-hidden">
      <div className="relative aspect-video bg-black">
        {isEmbeddable ? (
          <ReactPlayer
            url={src}
            playing={playing}
            width="100%"
            height="100%"
            controls={false}
            onEnded={() => setPlaying(false)}
          />
        ) : (
          <video
            ref={videoRef}
            src={src}
            className="w-full h-full object-contain"
            onEnded={() => setPlaying(false)}
          />
        )}
        <div className="absolute inset-0 flex items-center justify-center">
          <button
            onClick={togglePlay}
            className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            {playing ? (
              <Pause className="w-6 h-6 text-white" />
            ) : (
              <Play className="w-6 h-6 text-white ml-1" />
            )}
          </button>
        </div>
        {duration > 0 && (
          <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-0.5 rounded text-xs text-white">
            {formatTime(duration)}
          </div>
        )}
      </div>
      <div className="p-3 flex items-center justify-between">
        <div className="text-sm text-gray-400">
          {fileSize && <span>{fileSize}</span>}
        </div>
        <div className="flex gap-2">
          {onReplace && (
            <button onClick={onReplace} className="flex items-center gap-1 px-3 py-1.5 border border-white/20 rounded-lg text-white text-xs hover:bg-white/10 transition-colors">
              <RotateCcw className="w-3 h-3" />
              Replace
            </button>
          )}
          {onRemove && (
            <button onClick={onRemove} className="px-3 py-1.5 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-xs hover:bg-red-500/30 transition-colors">
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function VideoElementModal({
  isOpen,
  onClose,
  onSave,
  video,
  folders,
  currentFolder,
  skipFolderSelect = false,
  readOnly = false,
  onCopyToMyElements
}) {
  const [sourceTab, setSourceTab] = useState('upload');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    src_type: 'upload',
    file_url: '',
    thumbnail_url: '',
    duration: 0,
    file_size: '',
    display_mode: 'fullscreen',
    folder_id: currentFolder?.id || '',
    playback: {
      autoplay: true,
      loop: false,
      start_at: 0,
      end_at: null,
      audio_mode: 'normal',
      volume: 100
    },
    pip_settings: {
      position: 'top_left',
      size: 25
    }
  });

  const [isUploading, setIsUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlValidating, setUrlValidating] = useState(false);
  const [urlError, setUrlError] = useState('');
  const [error, setError] = useState('');
  const [hasInitialized, setHasInitialized] = useState(false);

  const [showAssetLibrary, setShowAssetLibrary] = useState(false);
  const fileInputRef = useRef(null);

  const handleAssetSelect = (asset) => {
    updateField('file_url', asset.file_url);
    updateField('src_type', 'upload');
    updateField('file_size', formatFileSize(asset.file_size || 0));
    if (!formData.name) {
      updateField('name', asset.name);
    }
    // Get video metadata
    const videoEl = document.createElement('video');
    videoEl.preload = 'metadata';
    videoEl.onloadedmetadata = () => {
      updateField('duration', Math.round(videoEl.duration));
      updateField('playback.end_at', Math.round(videoEl.duration));
    };
    videoEl.src = asset.file_url;
    setShowAssetLibrary(false);
  };

  // Initialize form data only when modal opens, not on every prop change
  useEffect(() => {
    if (isOpen && !hasInitialized) {
      if (video) {
        setFormData({
          ...formData,
          ...video,
          playback: { ...formData.playback, ...video.playback },
          pip_settings: { ...formData.pip_settings, ...video.pip_settings }
        });
        setSourceTab(video.src_type || 'upload');
        if (video.src_type === 'url') {
          setUrlInput(video.file_url);
        }
      } else {
        setFormData(prev => ({
          ...prev,
          folder_id: currentFolder?.id || '',
          name: '',
          file_url: '',
          duration: 0
        }));
      }
      setError('');
      setHasInitialized(true);
    }
  }, [isOpen, video, currentFolder, hasInitialized]);

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasInitialized(false);
    }
  }, [isOpen]);

  const updateField = (path, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setError('');

    const validTypes = ['video/mp4', 'video/quicktime', 'video/webm'];
    if (!validTypes.includes(file.type)) {
      setError('Unsupported format. Please use MP4, MOV, or WebM.');
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(`File too large. Maximum size is 1GB.`);
      return;
    }

    setIsUploading(true);

    try {
      const result = await uploadService.uploadFile(file);

      if (!result || !result.url) {
        throw new Error('No file URL returned from server');
      }

      updateField('file_url', result.url);
      updateField('src_type', 'upload');
      updateField('file_size', formatFileSize(file.size));

      if (!formData.name) {
        updateField('name', file.name.replace(/\.[^/.]+$/, ''));
      }

      // Get video metadata
      const videoEl = document.createElement('video');
      videoEl.preload = 'metadata';
      videoEl.onloadedmetadata = () => {
        updateField('duration', Math.round(videoEl.duration));
        updateField('playback.end_at', Math.round(videoEl.duration));
      };
      videoEl.src = result.url;

    } catch (err) {
      console.error('Upload error:', err);
      setError(`Upload failed: ${err.message || 'Unknown error'}. Please try again.`);
    } finally {
      setIsUploading(false);
    }
  };

  const isEmbeddableUrl = (url) => {
    const patterns = [
      /(?:youtube\.com\/(?:watch|embed|shorts)|youtu\.be\/)/i,
      /(?:vimeo\.com\/)/i,
      /(?:dailymotion\.com\/|dai\.ly\/)/i,
      /(?:facebook\.com\/.*\/videos\/)/i,
      /(?:twitch\.tv\/)/i,
      /(?:wistia\.com\/|wi\.st\/)/i,
      /(?:streamable\.com\/)/i,
    ];
    return patterns.some((p) => p.test(url));
  };

  const handleUrlValidate = async () => {
    if (!urlInput.trim()) return;

    setUrlValidating(true);
    setUrlError('');

    try {
      const url = new URL(urlInput);
      if (!['http:', 'https:'].includes(url.protocol)) {
        setUrlError('Please enter an HTTP or HTTPS URL.');
        setUrlValidating(false);
        return;
      }
    } catch {
      setUrlError('Please enter a valid URL.');
      setUrlValidating(false);
      return;
    }

    // For YouTube, Vimeo, and other embeddable URLs, accept directly
    // (ReactPlayer handles these natively — no need to validate as raw video)
    if (isEmbeddableUrl(urlInput)) {
      updateField('file_url', urlInput);
      updateField('src_type', 'url');
      setUrlValidating(false);
      return;
    }

    // For direct video file URLs, validate via HTML5 video element
    const videoEl = document.createElement('video');
    videoEl.src = urlInput;

    const timeout = setTimeout(() => {
      setUrlError('URL could not be reached. Please check the link.');
      setUrlValidating(false);
    }, 10000);

    videoEl.onloadedmetadata = () => {
      clearTimeout(timeout);
      updateField('file_url', urlInput);
      updateField('src_type', 'url');
      updateField('duration', Math.round(videoEl.duration));
      updateField('playback.end_at', Math.round(videoEl.duration));
      setUrlValidating(false);
    };

    videoEl.onerror = () => {
      clearTimeout(timeout);
      setUrlError('Could not load video. Check if URL is accessible.');
      setUrlValidating(false);
    };
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Please enter a name for the video.');
      return;
    }
    if (!formData.file_url) {
      setError('Please upload a video or enter a valid URL.');
      return;
    }

    await onSave(formData);
    onClose();
  };

  const isValid = formData.name.trim() && formData.file_url && (skipFolderSelect || formData.folder_id);

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#001233] border border-white/10 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6a1fbf] to-[#00c853] flex items-center justify-center">
                <Video className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {video ? 'Edit Video Element' : 'Add Video Element'}
                </h2>
                <p className="text-sm text-gray-400">Upload or link a video to play on stage</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {readOnly && (
              <div className="flex items-start gap-3 mx-6 mt-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                <p className="text-sm text-purple-200">
                  This element is from a shared library and is <strong>view only</strong>. Copy it to your library to make changes.
                </p>
              </div>
            )}
            <div className="p-6 space-y-6">
              {/* Error Display */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Source Selection */}
              <Tabs value={sourceTab} onValueChange={setSourceTab}>
                <TabsList className="grid grid-cols-3 w-full bg-white/5 rounded-lg p-1">
                  <TabsTrigger value="upload">
                    <Upload className="w-4 h-4" />
                    Upload
                  </TabsTrigger>
                  <TabsTrigger value="library">
                    <HardDrive className="w-4 h-4" />
                    Library
                  </TabsTrigger>
                  <TabsTrigger value="url">
                    <Link className="w-4 h-4" />
                    URL
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="upload" className="mt-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_FORMATS}
                    onChange={handleFileSelect}
                    className="hidden"
                  />

                  {!formData.file_url || formData.src_type !== 'upload' ? (
                    !readOnly && <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="w-full border-2 border-dashed border-white/20 rounded-xl p-8 text-center hover:border-[#6a1fbf]/50 transition-colors disabled:opacity-50"
                    >
                      {isUploading ? (
                        <div className="space-y-3">
                          <Loader2 className="w-10 h-10 text-[#6a1fbf] mx-auto animate-spin" />
                          <p className="text-white font-medium">Uploading...</p>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                          <p className="text-white font-medium">Click to upload video</p>
                          <p className="text-xs text-gray-400 mt-1">MP4, MOV, WebM • Max 1GB</p>
                        </>
                      )}
                    </button>
                  ) : (
                    <VideoPreview
                      src={formData.file_url}
                      duration={formData.duration}
                      fileSize={formData.file_size}
                      onRemove={readOnly ? undefined : () => {
                        updateField('file_url', '');
                        updateField('duration', 0);
                      }}
                      onReplace={readOnly ? undefined : () => fileInputRef.current?.click()}
                    />
                  )}
                </TabsContent>

                <TabsContent value="library" className="mt-4">
                  <button
                    onClick={() => setShowAssetLibrary(true)}
                    className="w-full border-2 border-dashed border-white/20 rounded-xl p-8 text-center hover:border-[#6a1fbf]/50 transition-colors"
                  >
                    <Video className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                    <p className="text-white font-medium">Browse Asset Library</p>
                    <p className="text-xs text-gray-400 mt-1">Select from your uploaded videos</p>
                  </button>
                </TabsContent>

                <TabsContent value="url" className="mt-4 space-y-4">
                  <div className="flex gap-2">
                    <input
                      value={urlInput}
                      onChange={(e) => {
                        setUrlInput(e.target.value);
                        setUrlError('');
                      }}
                      placeholder="https://example.com/video.mp4"
                      className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf] transition-colors"
                    />
                    <button
                      onClick={handleUrlValidate}
                      disabled={urlValidating || !urlInput.trim()}
                      className="px-4 py-2 bg-[#6a1fbf] hover:bg-[#5a1aa0] rounded-lg text-white text-sm transition-colors disabled:opacity-50"
                    >
                      {urlValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Validate'}
                    </button>
                  </div>

                  {urlError && <p className="text-sm text-red-400">{urlError}</p>}

                  {formData.file_url && formData.src_type === 'url' && (
                    <VideoPreview
                      src={formData.file_url}
                      duration={formData.duration}
                      onRemove={readOnly ? undefined : () => {
                        updateField('file_url', '');
                        setUrlInput('');
                        updateField('duration', 0);
                      }}
                    />
                  )}
                </TabsContent>
              </Tabs>

              {/* Video Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 mb-2 block text-sm">Video Name *</label>
                  <input
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="e.g., Intro Video"
                    disabled={readOnly}
                    className={`w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf] transition-colors ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                </div>

                {!skipFolderSelect && (
                  <div>
                    <label className="text-gray-400 mb-2 block text-sm">Folder *</label>
                    <Select value={formData.folder_id} onValueChange={(v) => updateField('folder_id', v)} placeholder="Select folder...">
                      {folders?.map(f => (
                        <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                      ))}
                    </Select>
                  </div>
                )}

                <div className="col-span-2">
                  <label className="text-gray-400 mb-2 block text-sm">Description (optional)</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="Internal notes about this video..."
                    disabled={readOnly}
                    className={`w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf] transition-colors h-20 resize-none ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                </div>
              </div>

              {/* Playback Settings */}
              <div className="space-y-4">
                <label className="text-white font-medium text-sm">Playback Settings</label>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Play className="w-4 h-4 text-[#6a1fbf]" />
                      <div>
                        <p className="text-white text-sm">Autoplay</p>
                        <p className="text-xs text-gray-400">Play when added to stage</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.playback.autoplay}
                      onCheckedChange={(v) => updateField('playback.autoplay', v)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Repeat className="w-4 h-4 text-[#00c853]" />
                      <div>
                        <p className="text-white text-sm">Loop</p>
                        <p className="text-xs text-gray-400">Repeat when finished</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.playback.loop}
                      onCheckedChange={(v) => updateField('playback.loop', v)}
                    />
                  </div>
                </div>

                {/* Start/End Time */}
                {formData.duration > 0 && (
                  <div className="p-4 bg-white/5 rounded-xl space-y-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <label className="text-white text-sm">Trim Video</label>
                      <span className="text-xs text-gray-500">(Duration: {formatTime(formData.duration)})</span>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">Start At</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={0}
                            max={formData.playback.end_at || formData.duration}
                            value={formData.playback.start_at || 0}
                            onChange={(e) => updateField('playback.start_at', parseInt(e.target.value) || 0)}
                            className="w-20 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white outline-none focus:border-[#6a1fbf]"
                          />
                          <span className="text-gray-400 text-sm">sec</span>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">End At</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={formData.playback.start_at || 0}
                            max={formData.duration}
                            value={formData.playback.end_at || formData.duration}
                            onChange={(e) => updateField('playback.end_at', parseInt(e.target.value) || formData.duration)}
                            className="w-20 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white outline-none focus:border-[#6a1fbf]"
                          />
                          <span className="text-gray-400 text-sm">sec</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Audio Mode */}
                <div className="space-y-3">
                  <label className="text-white text-sm">Audio Mode</label>
                  <div className="grid grid-cols-3 gap-2">
                    {AUDIO_MODES.map((mode) => (
                      <button
                        key={mode.id}
                        type="button"
                        onClick={() => updateField('playback.audio_mode', mode.id)}
                        className={`p-3 rounded-xl text-center transition-all ${
                          formData.playback.audio_mode === mode.id
                            ? 'bg-[#6a1fbf] ring-2 ring-[#6a1fbf]'
                            : 'bg-white/5 hover:bg-white/10'
                        }`}
                      >
                        <mode.icon className={`w-5 h-5 mx-auto mb-1 ${
                          formData.playback.audio_mode === mode.id ? 'text-white' : 'text-gray-400'
                        }`} />
                        <p className="text-sm font-medium text-white">{mode.name}</p>
                        <p className="text-[10px] text-gray-400">{mode.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Display Mode */}
                <div className="space-y-3">
                  <label className="text-white text-sm">Display Mode</label>
                  <div className="grid grid-cols-3 gap-2">
                    {DISPLAY_MODES.map((mode) => (
                      <button
                        key={mode.id}
                        type="button"
                        onClick={() => updateField('display_mode', mode.id)}
                        className={`p-3 rounded-xl text-center transition-all ${
                          formData.display_mode === mode.id
                            ? 'bg-[#6a1fbf] ring-2 ring-[#6a1fbf]'
                            : 'bg-white/5 hover:bg-white/10'
                        }`}
                      >
                        <p className="text-sm font-medium text-white">{mode.name}</p>
                        <p className="text-[10px] text-gray-400">{mode.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* PiP Settings */}
                {formData.display_mode === 'pip' && (
                  <div className="p-4 bg-white/5 rounded-xl space-y-4">
                    <label className="text-white text-sm">Picture-in-Picture Settings</label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">Position</label>
                        <Select
                          value={formData.pip_settings.position}
                          onValueChange={(v) => updateField('pip_settings.position', v)}
                          placeholder="Select position..."
                        >
                          <SelectItem value="top_left">Top Left</SelectItem>
                          <SelectItem value="top_right">Top Right</SelectItem>
                          <SelectItem value="bottom_left">Bottom Left</SelectItem>
                          <SelectItem value="bottom_right">Bottom Right</SelectItem>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">Size: {formData.pip_settings.size}%</label>
                        <Slider
                          value={[formData.pip_settings.size]}
                          onValueChange={([v]) => updateField('pip_settings.size', v)}
                          min={15}
                          max={40}
                          step={5}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-white/20 rounded-lg text-gray-300 hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            {readOnly ? (onCopyToMyElements && (
              <button
                type="button"
                onClick={onCopyToMyElements}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                Copy to My Elements
              </button>
            )) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={!isValid}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-4 h-4" />
                {video ? 'Save Changes' : 'Add Video'}
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return (
    <>
      {ReactDOM.createPortal(modalContent, document.body)}
      <AssetLibraryModal
        isOpen={showAssetLibrary}
        onClose={() => setShowAssetLibrary(false)}
        onSelect={handleAssetSelect}
        filterType="video"
      />
    </>
  );
}
